import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { formatDateTimeInViewerTZ } from '../../utils/dateTimeUtils';
import { listTime } from './threadMessages';
import { wa } from './waTheme';

const labelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.4,
  color: wa.meta,
  mb: 0.35,
};

const selectSx = {
  borderRadius: 1.5,
  fontSize: 13,
  '& .MuiSelect-select': {
    py: 0.6,
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

function FieldLabel({ children }) {
  return <Typography sx={labelSx}>{children}</Typography>;
}

function personName(person) {
  return [person.first_name, person.last_name].filter(Boolean).join(' ').trim() || person.email;
}

export default function ChatContextPanel({
  leads,
  pickedLead,
  onPickLead,
  statusOptions,
  onStage,
  subStatusOptions = [],
  subStatusKnown = false,
  onSubStatus,
  stageBusy,
  canAssign,
  assignees,
  assigneesLoading,
  assigneesError,
  onOwner,
  ownerBusy,
  lastReply,
  tz,
  now,
  windowOpen,
  expiresAt,
  remainingLabel,
  call,
  onOpenLead,
  onOpenLeads,
  phone,
  noteRef,
  shared,
}) {
  const closes = expiresAt ? formatDateTimeInViewerTZ(expiresAt, tz) : '';
  const history = (call?.items || []).filter((item) => item.id !== call?.missed?.id).slice(0, 3);
  const offer = call?.offer || { mode: 'loading' };
  const currentSubStatus = pickedLead?.sub_status || '';
  const subStatusChoices = currentSubStatus && !subStatusOptions.includes(currentSubStatus)
    ? [currentSubStatus, ...subStatusOptions]
    : subStatusOptions;
  const subStatusLocked = !pickedLead || stageBusy || !subStatusKnown || subStatusOptions.length === 0;

  return (
    <Box sx={{ width: 248, maxWidth: '100%', bgcolor: '#fff', overflow: 'auto', px: 1.5, py: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
        <FieldLabel>LEAD</FieldLabel>
        {pickedLead?.display_id ? (
          <Button
            size="small"
            onClick={onOpenLead}
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', color: wa.text, fontWeight: 700, fontSize: 12, minWidth: 0, py: 0 }}
          >
            {pickedLead.display_id}
          </Button>
        ) : (
          <Typography sx={{ fontSize: 12, color: wa.meta }}>No lead yet</Typography>
        )}
      </Stack>

      {leads.length > 1 && (
        <Box sx={{ mb: 1.25 }}>
          <FieldLabel>Which lead</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={pickedLead?.id || ''}
              onChange={(event) => onPickLead(event.target.value)}
              sx={selectSx}
            >
              {leads.map((lead) => (
                <MenuItem key={lead.id} value={lead.id}>
                  {lead.child_name || lead.display_id || 'Lead'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ mb: 1.25 }}>
        <FieldLabel>Stage</FieldLabel>
        <FormControl fullWidth size="small" disabled={!pickedLead || stageBusy}>
          <Select
            displayEmpty
            value={pickedLead?.status || ''}
            onChange={(event) => onStage(event.target.value)}
            sx={{ ...selectSx, bgcolor: '#fff' }}
            renderValue={(value) => statusOptions.find(([key]) => key === value)?.[1] || 'No stage'}
          >
            {statusOptions.map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 1 }}>
          <FieldLabel>Substatus</FieldLabel>
          <FormControl fullWidth size="small" disabled={subStatusLocked}>
            <Select
              displayEmpty
              value={currentSubStatus}
              onChange={(event) => onSubStatus?.(event.target.value)}
              sx={selectSx}
              renderValue={(value) => {
                if (value) return value;
                if (!subStatusKnown) return 'Loading…';
                return subStatusOptions.length ? 'None' : 'None for this stage';
              }}
            >
              <MenuItem value="">None</MenuItem>
              {subStatusChoices.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {!pickedLead && (
          <Box sx={{ mt: 0.75 }}>
            <Typography sx={{ fontSize: 12, color: wa.meta }}>
              {phone
                ? `This chat has no lead, so the stage and owner stay locked. Create a lead for ${phone}. The form opens with this number filled in.`
                : 'This chat has no lead, so the stage and owner stay locked. Create a lead for this number. The form opens with the number filled in.'}
            </Typography>
            {onOpenLeads && (
              <Button
                size="small"
                onClick={onOpenLeads}
                sx={{ textTransform: 'none', color: wa.green, fontWeight: 700, px: 0, mt: 0.25 }}
              >
                Create lead
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 1 }}>
        <FieldLabel>Owner</FieldLabel>
        <FormControl fullWidth size="small" disabled={!pickedLead || !canAssign || ownerBusy}>
          <Select
            displayEmpty
            value={pickedLead?.assigned_to || ''}
            onChange={(event) => onOwner(event.target.value)}
            sx={selectSx}
            renderValue={(value) => {
              if (!value) return 'Unassigned';
              const known = assignees.find((person) => String(person.user_id) === String(value));
              if (known) return personName(known);
              return pickedLead?.assigned_name || 'Assigned';
            }}
          >
            {!pickedLead?.assigned_to && <MenuItem value="" disabled>Unassigned</MenuItem>}
            {pickedLead?.assigned_to && !assignees.some((person) => String(person.user_id) === String(pickedLead.assigned_to)) && (
              <MenuItem value={pickedLead.assigned_to}>{pickedLead.assigned_name || 'Assigned'}</MenuItem>
            )}
            {assigneesLoading && <MenuItem value="__loading" disabled>Loading…</MenuItem>}
            {assignees.map((person) => (
              <MenuItem key={person.user_id} value={person.user_id}>
                {personName(person)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {assigneesError && (
          <Typography sx={{ fontSize: 12, color: wa.fail, mt: 0.5 }}>{assigneesError}</Typography>
        )}
        {pickedLead && canAssign && !assigneesLoading && !assigneesError && assignees.length > 0 && !pickedLead.assigned_to && (
          <Typography sx={{ fontSize: 11, color: wa.meta, mt: 0.35 }}>
            Choose a salesperson to assign this lead.
          </Typography>
        )}
        {pickedLead && !assigneesLoading && !assigneesError && assignees.length === 0 && canAssign && (
          <Typography sx={{ fontSize: 12, color: wa.meta, mt: 0.5 }}>
            {pickedLead.country
              ? `No salesperson or sales supervisor is active for ${pickedLead.country}. Add one under Users, then choose the owner here.`
              : 'This lead has no country, so an owner cannot be chosen. Set the country on the lead first.'}
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: 12, color: wa.meta, mb: 1 }}>
        {lastReply?.name
          ? `Last reply by ${lastReply.name}${lastReply.at ? `, ${listTime(lastReply.at, tz, now)}` : ''}`
          : 'No reply yet'}
      </Typography>
      {shared && (
        <Typography sx={{ fontSize: 11, color: wa.meta, mb: 1 }}>Shared chat</Typography>
      )}

      <Divider sx={{ mb: 1 }} />

      <FieldLabel>REPLY WINDOW</FieldLabel>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: wa.text, lineHeight: 1.2 }}>
        {windowOpen && remainingLabel ? `${remainingLabel} left` : 'Closed'}
      </Typography>
      <Typography sx={{ fontSize: 11, color: wa.meta, mt: 0.25, mb: 1 }}>
        {windowOpen && closes
          ? `Closes ${closes}. Then only templates can be sent.`
          : 'Only templates can be sent until the parent messages again.'}
      </Typography>

      {call?.enabled && (
        <>
          <Divider sx={{ mb: 1 }} />
          <FieldLabel>CALL REQUEST</FieldLabel>
          {!call.bpContactId && (
            <Typography sx={{ fontSize: 13, color: wa.meta }}>This chat is not linked to calling yet.</Typography>
          )}
          {call.bpContactId && offer.mode === 'loading' && call.permissionLoading && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mb: 1 }}>Checking whether you can call…</Typography>
          )}
          {call.bpContactId && offer.mode === 'call' && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mb: 1 }}>Calls are allowed. Use Call now.</Typography>
          )}
          {call.bpContactId && offer.mode === 'suggest' && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mb: 1 }}>
              Two calls in a row were not answered. Send a message instead.
            </Typography>
          )}
          {call.bpContactId && offer.mode === 'wait' && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mb: 1 }}>{call.waitText}</Typography>
          )}
          {call.bpContactId && offer.mode === 'hidden' && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mb: 1 }}>This parent asked not to be called.</Typography>
          )}
          {call.permissionError && (
            <Typography sx={{ fontSize: 13, color: wa.fail, mb: 1 }}>Could not check call permission.</Typography>
          )}
          <TextField
            inputRef={noteRef}
            fullWidth
            multiline
            minRows={2}
            label="Note for caller"
            value={call.reason}
            onChange={(event) => call.editReason(event.target.value)}
            inputProps={{ maxLength: 300 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
            <Button
              variant="outlined"
              onClick={call.ask}
              disabled={call.busy || !String(call.reason || '').trim() || offer.mode !== 'ask'}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                color: wa.green,
                borderColor: '#b7d7cf',
                fontWeight: 600,
              }}
            >
              Request call
            </Button>
            {offer.mode === 'hidden' ? (
              <Button
                size="small"
                onClick={() => call.setDoNotCall(false)}
                disabled={call.busy}
                sx={{ textTransform: 'none', color: wa.text }}
              >
                Allow calls
              </Button>
            ) : (
              <Button
                size="small"
                onClick={() => call.setDoNotCall(true)}
                disabled={call.busy || !call.bpContactId || offer.mode === 'loading'}
                sx={{ textTransform: 'none', color: wa.text }}
              >
                Mark do-not-call
              </Button>
            )}
          </Stack>
          {call.message && (
            <Typography sx={{ fontSize: 12, color: call.tone === 'error' ? wa.fail : wa.meta, mt: 1 }}>
              {call.message}
            </Typography>
          )}
          {history.map((item) => (
            <Typography key={item.id} sx={{ fontSize: 12, color: wa.meta, mt: 0.75 }}>
              {item.summary}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}
